/**
 * Documents page (OPS 3.0a + 3.0b) — internal filing cabinet.
 *
 * Client tree (scoped, searchable) → per-client areas (Legal / Engagements /
 * Compliance / Proposals / Invoices) → file list with side-by-side preview.
 * All areas read the SAME unified store (client_documents). Legal renders the
 * existing vault section (extraction + review gate + status/expiry pills).
 * Engagements takes manual uploads (clients.edit). Compliance shows
 * jurisdiction folders → per-item evidence folders (3.0b, upload/delete via
 * compliance.edit). Proposals/Invoices are auto-filed (read-only here).
 */

import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CLIENT_AREA_LABELS,
  CLIENT_DOCUMENT_STATUS_LABELS,
  JURISDICTION_LABELS,
  type ClientDocumentArea,
  type ClientDocumentOutput,
  type ClientDocumentStatus,
  type ClientOutput,
  type DocumentTreeEntry,
} from '@1financial/shared';
import { getAccessToken, authenticatedFetch as fetch } from '../lib/auth-context.js';
import { httpError } from '../lib/http-error.js';
import { useCapabilities } from '../hooks/useCapabilities.js';
import { fmtDate } from '../lib/date.js';
import { ClientDocumentsSection } from '../components/ClientDocumentsSection.js';
import { ComplianceEvidenceFolders } from '../components/ComplianceEvidence.js';
import { ExpiryBadge } from '../components/ExpiryBadge.js';
import { useUserPreference } from '../lib/user-preferences.js';

const AREAS: ClientDocumentArea[] = ['LEGAL', 'ENGAGEMENTS', 'COMPLIANCE', 'PROPOSALS', 'INVOICES'];
const MAX_FILE_BYTES = 20 * 1024 * 1024;
// Office formats: uploadable, but download-only (the API forces attachment).
export const OFFICE_MIME = /^application\/(msword|vnd\.openxmlformats-officedocument\.|vnd\.ms-excel|vnd\.ms-powerpoint)/;
export const DOCS_ACCEPT = 'application/pdf,image/jpeg,image/jpg,image/png,image/webp,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation';

function authHeaders(): HeadersInit {
  return { Authorization: `Bearer ${getAccessToken() ?? ''}` };
}

const STATUS_COLOURS: Record<ClientDocumentStatus, string> = {
  PENDING_REVIEW: 'text-amber-300 border-amber-500/40 bg-amber-500/10',
  EXTRACTION_FAILED: 'text-rose-300 border-rose-500/40 bg-rose-500/10',
  CONFIRMED: 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10',
  SUPERSEDED: 'text-muted border-border bg-bg-base',
};

export function DocumentsPage() {
  const { canDo } = useCapabilities();
  const visibleAreas = AREAS.filter((area) => area === 'INVOICES' ? canDo('billing.view') : area === 'PROPOSALS' ? canDo('proposals.view') && canDo('billing.view') : true);
  const [search, setSearch] = useState('');
  const [jurisdiction, setJurisdiction] = useUserPreference('documents.jurisdiction', '');
  const [clientId, setClientId] = useState<string | null>(null);
  const [area, setArea] = useState<ClientDocumentArea | null>(null);
  const [historicMode, setHistoricMode] = useState(false);
  const [historicArea, setHistoricArea] = useState<ClientDocumentArea | null>(null);

  const tree = useQuery({
    queryKey: ['documents-tree'],
    queryFn: async () => {
      const res = await fetch('/v1/documents/tree', { headers: authHeaders() });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: { items: DocumentTreeEntry[] } };
      return body.data.items;
    },
  });

  // Jurisdiction filter options = the values actually present in the scoped tree.
  const jurisdictions = [...new Set((tree.data ?? []).map((c) => c.jurisdiction).filter((j): j is string => j !== null))].sort();

  const filtered = (tree.data ?? []).filter((c) => {
    // Selecting a jurisdiction excludes null-jurisdiction clients (cleaner than an "Unspecified" bucket).
    if (jurisdiction && c.jurisdiction !== jurisdiction) return false;
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return c.name.toLowerCase().includes(q) || (c.company ?? '').toLowerCase().includes(q);
  });

  const selected = (tree.data ?? []).find((c) => c.id === clientId) ?? null;

  return (
    <div className="flex flex-col xl:flex-row gap-4 w-full h-full min-h-0">
      {/* Client tree */}
      <aside className="w-full xl:w-72 shrink-0 bg-bg-panel border border-border rounded-xl flex flex-col min-h-0 max-h-80 xl:max-h-[calc(100vh-7rem)]">
        <div className="p-3 border-b border-border space-y-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search clients…"
            className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white placeholder-muted focus:outline-none focus:border-accent"
          />
          <select
            value={jurisdiction}
            onChange={(e) => setJurisdiction(e.target.value)}
            className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent"
          >
            <option value="">All jurisdictions</option>
            {jurisdictions.map((j) => (
              <option key={j} value={j}>{JURISDICTION_LABELS[j as keyof typeof JURISDICTION_LABELS] ?? j}</option>
            ))}
          </select>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
          {tree.isLoading && <p className="text-muted text-sm p-3">Loading clients…</p>}
          {tree.error && <p className="text-rose-400 text-sm p-3">{(tree.error as Error).message}</p>}
          {filtered.map((c) => {
            const total = Object.values(c.counts).reduce((a, b) => a + b, 0);
            return (
              <button
                key={c.id}
                onClick={() => { setClientId(c.id); setArea(null); setHistoricMode(false); setHistoricArea(null); }}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${clientId === c.id ? 'bg-accent/15 text-white border border-accent/40' : 'text-muted hover:text-white hover:bg-bg-base border border-transparent'}`}
              >
                <span className="flex items-center justify-between gap-2">
                  <span className="truncate">{c.name}</span>
                  <span className="text-xs text-muted shrink-0">{total}</span>
                </span>
                {c.company && <span className="block text-xs text-muted truncate">{c.company}</span>}
              </button>
            );
          })}
          {tree.data && filtered.length === 0 && <p className="text-muted text-sm p-3">No clients match.</p>}
        </div>
      </aside>

      {/* Area folders + file list */}
      <div className="flex-1 min-w-0 bg-bg-panel border border-border rounded-xl p-5 overflow-y-auto">
        {!selected && (
          <div className="h-full flex items-center justify-center text-muted text-sm">
            Select a client to browse their document areas.
          </div>
        )}
        {selected && !historicMode && (
          <>
            <h1 className="text-base font-semibold text-white mb-1">{selected.name}</h1>
            <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 mb-5 mt-3">
              {visibleAreas.map((a) => (
                <button
                  key={a}
                  onClick={() => setArea(a)}
                  className={`rounded-lg border px-3 py-3 text-left transition-colors ${area === a ? 'border-accent bg-accent/10' : 'border-border bg-bg-base hover:border-accent/50'}`}
                >
                  <span className="block text-sm text-white">{CLIENT_AREA_LABELS[a]}</span>
                  <span className="block text-xs text-muted mt-0.5">{selected.counts[a] ?? 0} file{(selected.counts[a] ?? 0) === 1 ? '' : 's'}</span>
                </button>
              ))}
              <button
                onClick={() => { setHistoricMode(true); setArea(null); setHistoricArea(null); }}
                className="rounded-lg border border-border bg-bg-base hover:border-accent/50 px-3 py-3 text-left transition-colors"
              >
                <span className="block text-sm text-white">🗄️ Historic</span>
                <span className="block text-xs text-muted mt-0.5">
                  {Object.values(selected.historicCounts).reduce((a, b) => a + b, 0)} file{Object.values(selected.historicCounts).reduce((a, b) => a + b, 0) === 1 ? '' : 's'}
                </span>
              </button>
            </div>
            {area === 'LEGAL' && <LegalArea clientId={selected.id} />}
            {area === 'COMPLIANCE' && <ComplianceEvidenceFolders clientId={selected.id} />}
            {area && area !== 'LEGAL' && area !== 'COMPLIANCE' && (
              <AreaFileList clientId={selected.id} area={area} allowUpload={area === 'ENGAGEMENTS'} />
            )}
          </>
        )}
        {selected && historicMode && (
          <>
            <button onClick={() => { setHistoricMode(false); setHistoricArea(null); }} className="text-xs text-muted hover:text-white mb-2">← Back to live areas</button>
            <h1 className="text-base font-semibold text-white mb-1">🗄️ Historic — {selected.name}</h1>
            <p className="text-xs text-muted mb-4">Archive copies only — nothing is extracted, reviewed or auto-filed here.</p>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-5">
              {visibleAreas.map((a) => (
                <button
                  key={a}
                  onClick={() => setHistoricArea(a)}
                  className={`rounded-lg border px-3 py-3 text-left transition-colors ${historicArea === a ? 'border-accent bg-accent/10' : 'border-border bg-bg-base hover:border-accent/50'}`}
                >
                  <span className="block text-sm text-white">{CLIENT_AREA_LABELS[a]}</span>
                  <span className="block text-xs text-muted mt-0.5">{selected.historicCounts[a] ?? 0} file{(selected.historicCounts[a] ?? 0) === 1 ? '' : 's'}</span>
                </button>
              ))}
            </div>
            {historicArea && <HistoricFileList clientId={selected.id} area={historicArea} />}
          </>
        )}
      </div>
    </div>
  );
}

/** Legal area = the existing vault UX (review gate, extraction, pills). */
function LegalArea({ clientId }: { clientId: string }) {
  const client = useQuery({
    queryKey: ['client', clientId],
    queryFn: async () => {
      const res = await fetch(`/v1/clients/${clientId}`, { headers: authHeaders() });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: ClientOutput };
      return body.data;
    },
  });
  if (client.isLoading) return <p className="text-muted text-sm p-3">Loading…</p>;
  if (client.error || !client.data) return <p className="text-rose-400 text-sm p-3">{(client.error as Error)?.message ?? 'Failed to load client'}</p>;
  return <ClientDocumentsSection client={client.data} />;
}

async function fetchAreaDocuments(clientId: string, area: ClientDocumentArea): Promise<ClientDocumentOutput[]> {
  const res = await fetch(`/v1/documents/client/${clientId}?area=${area}`, { headers: authHeaders() });
  if (!res.ok) throw await httpError(res);
  const body = await res.json() as { ok: true; data: { items: ClientDocumentOutput[] } };
  return body.data.items;
}

function AreaFileList({ clientId, area, allowUpload }: { clientId: string; area: ClientDocumentArea; allowUpload: boolean }) {
  const queryClient = useQueryClient();
  const { canDo } = useCapabilities();
  const [filter, setFilter] = useState('');
  const [sortBy, setSortBy] = useState<'date' | 'name'>('date');
  const [sortAsc, setSortAsc] = useState(false);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [showUpload, setShowUpload] = useState(false);

  const docs = useQuery({
    queryKey: ['area-documents', clientId, area],
    queryFn: () => fetchAreaDocuments(clientId, area),
  });

  const items = (docs.data ?? [])
    .filter((d) => {
      const q = filter.trim().toLowerCase();
      if (!q) return true;
      return d.originalName.toLowerCase().includes(q)
        || (d.invoiceNumber ?? '').toLowerCase().includes(q)
        || (d.proposalSubject ?? '').toLowerCase().includes(q);
    })
    .sort((a, b) => {
      const cmp = sortBy === 'name'
        ? a.originalName.localeCompare(b.originalName)
        : a.createdAt.localeCompare(b.createdAt);
      return sortAsc ? cmp : -cmp;
    });

  const previewDoc = items.find((d) => d.id === previewId) ?? (items.length > 0 ? items[0] : null);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter files…"
          className="bg-bg-base border border-border rounded-lg px-3 py-1.5 text-sm text-white placeholder-muted focus:outline-none focus:border-accent"
        />
        <button
          onClick={() => { if (sortBy === 'date') { setSortAsc(!sortAsc); } else { setSortBy('date'); setSortAsc(false); } }}
          className={`text-xs px-2 py-1.5 rounded border ${sortBy === 'date' ? 'border-accent text-accent' : 'border-border text-muted hover:text-white'}`}
        >
          Date {sortBy === 'date' ? (sortAsc ? '↑' : '↓') : ''}
        </button>
        <button
          onClick={() => { if (sortBy === 'name') { setSortAsc(!sortAsc); } else { setSortBy('name'); setSortAsc(true); } }}
          className={`text-xs px-2 py-1.5 rounded border ${sortBy === 'name' ? 'border-accent text-accent' : 'border-border text-muted hover:text-white'}`}
        >
          Name {sortBy === 'name' ? (sortAsc ? '↑' : '↓') : ''}
        </button>
        <div className="flex-1" />
        {allowUpload && canDo('clients.edit') && (
          <button
            onClick={() => setShowUpload(true)}
            className="bg-accent hover:bg-emerald-400 text-accent-fg text-sm font-medium px-3 py-1.5 rounded-lg"
          >
            Upload
          </button>
        )}
      </div>

      {docs.isLoading && <p className="text-muted text-sm p-3">Loading files…</p>}
      {docs.error && <p className="text-rose-400 text-sm p-3">{(docs.error as Error).message}</p>}
      {docs.data && items.length === 0 && (
        <p className="text-muted text-sm p-3">
          {area === 'INVOICES' ? 'Issued invoice PDFs are filed here automatically.' : area === 'PROPOSALS' ? 'Sent proposal PDFs are filed here automatically.' : 'No files yet.'}
        </p>
      )}

      {items.length > 0 && (
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="min-w-0 lg:w-1/2 overflow-x-auto">
            <table className="w-full min-w-[520px] table-fixed text-sm">
              <thead>
                <tr className="text-left text-xs text-muted border-b border-border">
                  <th className="py-2 pr-3 font-medium w-2/5">File</th>
                  <th className="py-2 pr-3 font-medium">Linked</th>
                  <th className="py-2 pr-3 font-medium">Status</th>
                  <th className="py-2 font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {items.map((d) => (
                  <tr
                    key={d.id}
                    onClick={() => setPreviewId(d.id)}
                    className={`border-b border-border/50 cursor-pointer ${previewDoc?.id === d.id ? 'bg-accent/5' : 'hover:bg-bg-base'}`}
                  >
                    <td className="py-2 pr-3 text-white max-w-0">
                      <span className="block truncate">{d.originalName}</span>
                      {d.expiryDate && <span className="block mt-0.5"><ExpiryBadge expiryDate={d.expiryDate} /></span>}
                    </td>
                    <td className="py-2 pr-3 text-muted text-xs">
                      {d.invoiceNumber ?? d.proposalSubject ?? (d.uploadedByName ? `by ${d.uploadedByName}` : '—')}
                    </td>
                    <td className="py-2 pr-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-md border text-xs ${STATUS_COLOURS[d.status]}`}>
                        {CLIENT_DOCUMENT_STATUS_LABELS[d.status]}
                      </span>
                    </td>
                    <td className="py-2 text-muted text-xs whitespace-nowrap">{fmtDate(d.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="min-w-0 lg:w-1/2">
            {previewDoc && <StoreDocPreview doc={previewDoc} />}
          </div>
        </div>
      )}

      {showUpload && (
        <EngagementUploadModal
          clientId={clientId}
          onClose={() => setShowUpload(false)}
          onDone={() => {
            setShowUpload(false);
            void queryClient.invalidateQueries({ queryKey: ['area-documents', clientId, area] });
            void queryClient.invalidateQueries({ queryKey: ['documents-tree'] });
          }}
        />
      )}
    </div>
  );
}

/** Side-by-side preview — authed blob fetch, same pattern as the vault viewer. */
function StoreDocPreview({ doc }: { doc: ClientDocumentOutput }) {
  const isOffice = OFFICE_MIME.test(doc.mime);
  const preview = useQuery({
    queryKey: ['store-doc-file', doc.id],
    enabled: !isOffice,
    queryFn: async () => {
      const res = await fetch(`/v1/legal-documents/${doc.id}/file`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      return URL.createObjectURL(await res.blob());
    },
    staleTime: Infinity,
  });
  useEffect(() => () => { if (preview.data) URL.revokeObjectURL(preview.data); }, [preview.data]);

  const isImage = doc.mime.startsWith('image/');
  return (
    <div className="bg-bg-base border border-border rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border">
        <span className="text-xs text-muted truncate">{doc.originalName}</span>
        <span className="flex items-center gap-2 shrink-0">
          {doc.expiryDate && <ExpiryBadge expiryDate={doc.expiryDate} />}
        <a
          href={`/v1/legal-documents/${doc.id}/download`}
          onClick={(e) => {
            e.preventDefault();
            void (async () => {
              const res = await fetch(`/v1/legal-documents/${doc.id}/download`, { headers: authHeaders() });
              if (!res.ok) return;
              const url = URL.createObjectURL(await res.blob());
              const a = document.createElement('a');
              a.href = url;
              a.download = doc.originalName;
              a.click();
              URL.revokeObjectURL(url);
            })();
          }}
          className="text-xs text-accent hover:underline shrink-0 ml-2"
        >
          Download
        </a>
        </span>
      </div>
      <div className="min-h-[240px] flex items-center justify-center">
        {isOffice && (
          <div className="p-6 text-center">
            <p className="text-sm text-muted mb-3">Office documents can’t be previewed — download to open.</p>
            <span className="text-xs text-muted">Use the Download button above.</span>
          </div>
        )}
        {!isOffice && preview.isLoading && <span className="text-muted text-sm p-6">Loading preview…</span>}
        {preview.error && <span className="text-rose-400 text-sm p-6">{(preview.error as Error).message}</span>}
        {preview.data && isImage && <img src={preview.data} alt={doc.originalName} className="max-h-[60vh] w-full object-contain" />}
        {preview.data && !isImage && <iframe src={preview.data} title={doc.originalName} className="w-full h-[60vh]" />}
      </div>
    </div>
  );
}

/** Engagements area: plain manual upload (engagement letters signed externally). */
function EngagementUploadModal({ clientId, onClose, onDone }: { clientId: string; onClose: () => void; onDone: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleUpload(e: React.FormEvent) {
    e.preventDefault();
    if (!file) { setError('Choose a file first.'); return; }
    if (file.size > MAX_FILE_BYTES) { setError('File too large — max 20 MB.'); return; }
    setError(null);
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('area', 'ENGAGEMENTS');
      fd.append('file', file);
      const res = await fetch(`/v1/documents/client/${clientId}/upload`, { method: 'POST', headers: authHeaders(), body: fd });
      const body = await res.json() as { ok?: boolean; error?: { message?: string } };
      if (!res.ok || !body.ok) {
        setError(body.error?.message ?? `Error ${res.status}: ${res.statusText}`);
        return;
      }
      onDone();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <form onSubmit={handleUpload} className="bg-bg-panel border border-border rounded-xl w-full max-w-md p-5 space-y-3 shadow-2xl animate-pop">
        <h3 className="text-sm font-semibold text-white">File under Engagements</h3>
        <p className="text-xs text-muted">
          Engagement letters are signed externally (Documenso) — file the signed copy here. PDF, JPG, PNG or WebP, max 20 MB.
        </p>
        <input
          type="file"
          accept={DOCS_ACCEPT}
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          className="w-full text-sm text-muted file:mr-3 file:bg-bg-base file:border file:border-border file:rounded-lg file:px-3 file:py-1.5 file:text-sm file:text-white hover:file:border-accent"
        />
        {error && <p className="text-xs text-rose-400 bg-rose-950/30 border border-rose-800 rounded-lg px-3 py-2">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button>
          <button
            type="submit"
            disabled={submitting || !file}
            className="bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-sm font-medium px-4 py-1.5 rounded-lg"
          >
            {submitting ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Historic archive (per-client, manual-only) ────────────────────────────────

const HISTORIC_MAX_BYTES = 250 * 1024 * 1024;
const HISTORIC_MIME = /^(application\/pdf|image\/(jpeg|jpg|png|webp)|application\/(msword|vnd\.openxmlformats-officedocument\.|vnd\.ms-excel|vnd\.ms-powerpoint))/;

function HistoricFileList({ clientId, area }: { clientId: string; area: ClientDocumentArea }) {
  const queryClient = useQueryClient();
  const { canDo } = useCapabilities();
  const [filter, setFilter] = useState('');
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const docs = useQuery({
    queryKey: ['historic-documents', clientId, area],
    queryFn: async () => {
      const res = await fetch(`/v1/documents/client/${clientId}?area=${area}&historic=true`, { headers: authHeaders() });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: { items: ClientDocumentOutput[] } };
      return body.data.items;
    },
  });

  const items = (docs.data ?? []).filter((d) => {
    const q = filter.trim().toLowerCase();
    if (!q) return true;
    return d.originalName.toLowerCase().includes(q) || (d.uploadedByName ?? '').toLowerCase().includes(q);
  });
  const previewDoc = items.find((d) => d.id === previewId) ?? (items.length > 0 ? items[0] : null);

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['historic-documents', clientId, area] });
    void queryClient.invalidateQueries({ queryKey: ['documents-tree'] });
  }

  async function handleDelete(d: ClientDocumentOutput) {
    if (!window.confirm(`Remove archived "${d.originalName}"? The file is deleted from the store.`)) return;
    setError(null);
    setDeletingId(d.id);
    try {
      const res = await fetch(`/v1/documents/historic/${d.id}`, { method: 'DELETE', headers: authHeaders() });
      if (!res.ok) throw await httpError(res);
      invalidate();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter archived files…"
          className="bg-bg-base border border-border rounded-lg px-3 py-1.5 text-sm text-white placeholder-muted focus:outline-none focus:border-accent"
        />
        <div className="flex-1" />
        {canDo('clients.edit') && (
          <button
            onClick={() => setShowUpload(true)}
            className="bg-accent hover:bg-emerald-400 text-accent-fg text-sm font-medium px-3 py-1.5 rounded-lg"
          >
            Upload archive files
          </button>
        )}
      </div>
      {error && <p className="text-xs text-rose-400">{error}</p>}
      {docs.isLoading && <p className="text-muted text-sm p-3">Loading archived files…</p>}
      {docs.error && <p className="text-rose-400 text-sm p-3">{(docs.error as Error).message}</p>}
      {docs.data && items.length === 0 && (
        <p className="text-muted text-sm p-3">No archived files in this folder yet — upload old records here (multi-file and whole folders supported).</p>
      )}
      {items.length > 0 && (
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="min-w-0 lg:w-1/2 overflow-x-auto">
            <table className="w-full min-w-[520px] table-fixed text-sm">
              <thead>
                <tr className="text-left text-xs text-muted border-b border-border">
                  <th className="py-2 pr-3 font-medium w-2/5">File</th>
                  <th className="py-2 pr-3 font-medium">Archived by</th>
                  <th className="py-2 pr-3 font-medium">Date</th>
                  <th className="py-2 font-medium" />
                </tr>
              </thead>
              <tbody>
                {items.map((d) => (
                  <tr
                    key={d.id}
                    onClick={() => setPreviewId(d.id)}
                    className={`border-b border-border/50 cursor-pointer ${previewDoc?.id === d.id ? 'bg-accent/5' : 'hover:bg-bg-base'}`}
                  >
                    <td className="py-2 pr-3 text-white max-w-0 truncate">{d.originalName}</td>
                    <td className="py-2 pr-3 text-muted text-xs">{d.uploadedByName ?? '—'}</td>
                    <td className="py-2 pr-3 text-muted text-xs whitespace-nowrap">{fmtDate(d.createdAt)}</td>
                    <td className="py-2 text-right">
                      {canDo('clients.edit') && (
                        <button
                          onClick={(e) => { e.stopPropagation(); void handleDelete(d); }}
                          disabled={deletingId === d.id}
                          className="text-xs btn-ghost-danger border px-2 py-0.5 rounded disabled:opacity-50"
                        >
                          {deletingId === d.id ? '…' : 'Delete'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="min-w-0 lg:w-1/2">
            {previewDoc && <StoreDocPreview doc={previewDoc} />}
          </div>
        </div>
      )}
      {showUpload && (
        <BulkHistoricUploadModal
          clientId={clientId}
          area={area}
          onClose={() => setShowUpload(false)}
          onDone={() => { setShowUpload(false); invalidate(); }}
        />
      )}
    </div>
  );
}

type UploadEntry = { file: File; name: string; status: 'pending' | 'uploading' | 'done' | 'failed'; error?: string };

/** Bulk archive upload — multi-file select AND whole-folder (webkitdirectory).
 * Files upload sequentially, one request each, so per-file progress is exact
 * and a failed file never strands a store row without bytes. */
function BulkHistoricUploadModal({ clientId, area, onClose, onDone }: {
  clientId: string;
  area: ClientDocumentArea;
  onClose: () => void;
  onDone: () => void;
}) {
  const [entries, setEntries] = useState<UploadEntry[]>([]);
  const [running, setRunning] = useState(false);

  function addFiles(files: FileList | null) {
    if (!files) return;
    const next = [...entries];
    for (const f of Array.from(files)) {
      const rel = (f as File & { webkitRelativePath?: string }).webkitRelativePath;
      next.push({ file: f, name: rel && rel.trim() ? rel : f.name, status: 'pending' });
    }
    setEntries(next);
  }

  async function run() {
    setRunning(true);
    const next = [...entries];
    for (let i = 0; i < next.length; i++) {
      const e = next[i]!;
      if (e.status === 'done') continue;
      if (e.file.size > HISTORIC_MAX_BYTES) {
        next[i] = { ...e, status: 'failed', error: 'Too large — max 250 MB' };
        setEntries([...next]);
        continue;
      }
      if (!HISTORIC_MIME.test(e.file.type)) {
        next[i] = { ...e, status: 'failed', error: 'Unsupported type (pdf, jpg, png, webp, doc, docx, xls, xlsx, ppt, pptx)' };
        setEntries([...next]);
        continue;
      }
      next[i] = { ...e, status: 'uploading' };
      setEntries([...next]);
      try {
        const fd = new FormData();
        fd.append('area', area);
        fd.append('relativePath', e.name);
        fd.append('file', e.file);
        const res = await fetch(`/v1/documents/client/${clientId}/upload-historic`, { method: 'POST', headers: authHeaders(), body: fd });
        const body = await res.json() as { ok?: boolean; error?: { message?: string } };
        if (!res.ok || !body.ok) {
          next[i] = { ...next[i]!, status: 'failed', error: body.error?.message ?? `Error ${res.status}` };
        } else {
          next[i] = { ...next[i]!, status: 'done' };
        }
      } catch (err) {
        next[i] = { ...next[i]!, status: 'failed', error: (err as Error).message };
      }
      setEntries([...next]);
    }
    setRunning(false);
    if (next.every((e) => e.status === 'done')) onDone();
  }

  const doneCount = entries.filter((e) => e.status === 'done').length;
  const pending = entries.some((e) => e.status === 'pending' || e.status === 'failed');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-bg-panel border border-border rounded-xl w-full max-w-lg p-5 space-y-3 shadow-2xl animate-pop max-h-[85vh] overflow-y-auto">
        <h3 className="text-sm font-semibold text-white">Archive files under Historic → {CLIENT_AREA_LABELS[area]}</h3>
        <p className="text-xs text-muted">
          Pick many files or a whole folder (e.g. an old Nextcloud client folder). PDF, JPG, PNG, WebP or Office (doc/docx/xls/xlsx/ppt/pptx — download-only), max 250 MB per file.
          Folder structure is preserved in the file names.
        </p>
        <div className="flex gap-2">
          <label className="flex-1 text-center text-sm border border-dashed border-border hover:border-accent rounded-lg px-3 py-3 cursor-pointer text-muted hover:text-white">
            Select files
            <input type="file" multiple accept={DOCS_ACCEPT} className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
          </label>
          <label className="flex-1 text-center text-sm border border-dashed border-border hover:border-accent rounded-lg px-3 py-3 cursor-pointer text-muted hover:text-white">
            Select folder
            <input
              type="file"
              multiple
              className="hidden"
              ref={(el) => { if (el) el.setAttribute('webkitdirectory', ''); }}
              onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
            />
          </label>
        </div>
        {entries.length > 0 && (
          <ul className="divide-y divide-border/50 text-sm max-h-64 overflow-y-auto">
            {entries.map((e, i) => (
              <li key={i} className="flex items-center justify-between gap-3 py-1.5">
                <span className="text-white truncate">{e.name}</span>
                <span className={`text-xs shrink-0 ${e.status === 'done' ? 'text-emerald-300' : e.status === 'failed' ? 'text-rose-400' : e.status === 'uploading' ? 'text-accent' : 'text-muted'}`}>
                  {e.status === 'done' ? '✓' : e.status === 'failed' ? (e.error ?? 'Failed') : e.status === 'uploading' ? 'Uploading…' : 'Pending'}
                </span>
              </li>
            ))}
          </ul>
        )}
        {entries.length > 0 && <p className="text-xs text-muted">{doneCount}/{entries.length} archived</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={running} className="text-sm text-muted hover:text-white px-3 py-1.5 disabled:opacity-50">
            {doneCount > 0 && !pending ? 'Close' : 'Cancel'}
          </button>
          <button
            onClick={() => void run()}
            disabled={running || entries.length === 0 || !pending}
            className="bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-sm font-medium px-4 py-1.5 rounded-lg"
          >
            {running ? 'Uploading…' : `Archive ${entries.filter((e) => e.status !== 'done').length} file${entries.filter((e) => e.status !== 'done').length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  );
}
